import { newApiBaseUrl } from "../../../../axiosInstance";
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Search as SearchIcon, X } from "lucide-react";
import axios from "axios";
import ShowCategory from "./showCategory";
import {
  buildSearchVisibleIds,
  extractCategorySlugsFromProducts,
  isAtRootLevel,
  isInSearchVisible,
  isOrphanCategory,
} from "./categorySearchUtils";
import {
  resetExpandedCards,
  setExpandedCardsAction,
} from "../../../server/Category";
import ReactTooltip from "react-tooltip";

const PRODUCT_SEARCH_MIN_LENGTH = 2;
const PRODUCT_SEARCH_DEBOUNCE_MS = 350;

const CategoriesEdit = () => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [productMatchSlugs, setProductMatchSlugs] = useState([]);
  const productSearchRequestRef = useRef(0);
  const categoriesState = useSelector((state) => state.category.categories);
  const stationId = useSelector((state) => state.user.stationId);
  const [openSections, setOpenSections] = useState({
    inactive: false,
    orphans: false,
  });

  const searchFun = (e) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearch("");
    setProductMatchSlugs([]);
  };

  const normalizedSearch = search.trim().toLowerCase();
  const isSearchActive = normalizedSearch.length > 0;

  const searchVisibleIds = useMemo(
    () =>
      buildSearchVisibleIds(categoriesState, {
        query: normalizedSearch,
        slugMatches: productMatchSlugs,
        activeOnly: true,
      }),
    [categoriesState, normalizedSearch, productMatchSlugs],
  );

  useEffect(() => {
    if (
      !normalizedSearch ||
      normalizedSearch.length < PRODUCT_SEARCH_MIN_LENGTH
    ) {
      setProductMatchSlugs([]);
      return undefined;
    }

    if (!stationId) return undefined;

    const requestId = ++productSearchRequestRef.current;
    const timeoutId = setTimeout(async () => {
      try {
        const res = await axios.get(`${newApiBaseUrl}/products/search`, {
          headers: {
            Authorization: `Bearer ${localStorage.jwtToken}`,
            "X-Tenant": localStorage.tenant || "hamazon",
          },
          params: {
            q: normalizedSearch,
            branch: stationId,
            fields: "gtin,name",
          },
        });

        if (requestId !== productSearchRequestRef.current) return;

        const results = Array.isArray(res.data?.results)
          ? res.data.results
          : [];
        setProductMatchSlugs(extractCategorySlugsFromProducts(results));
      } catch {
        if (requestId === productSearchRequestRef.current) {
          setProductMatchSlugs([]);
        }
      }
    }, PRODUCT_SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
      productSearchRequestRef.current += 1;
    };
  }, [normalizedSearch, stationId]);

  useEffect(() => {
    if (!isSearchActive) {
      dispatch(resetExpandedCards());
      return;
    }
    if (!searchVisibleIds?.size) {
      dispatch(resetExpandedCards());
      return;
    }

    const expandMap = {};
    searchVisibleIds.forEach((id) => {
      expandMap[id] = true;
    });
    dispatch(setExpandedCardsAction(expandMap));
  }, [isSearchActive, searchVisibleIds, dispatch]);

  const filteredCategories = useMemo(() => {
    const roots = categoriesState.filter(
      (category) => isAtRootLevel(category) && category.status === "ACTIVE",
    );
    if (!isSearchActive || !searchVisibleIds) return roots;
    return roots.filter((category) =>
      isInSearchVisible(searchVisibleIds, category),
    );
  }, [categoriesState, isSearchActive, searchVisibleIds]);

  const toggleSection = (sectionKey) => {
    setOpenSections((prevState) => ({
      ...prevState,
      [sectionKey]: !prevState[sectionKey],
    }));
  };

  const allInactiveAndDescendants = useMemo(() => {
    const inactivRootIds = new Set(
      categoriesState
        .filter((cat) => cat.status === "INACTIVE" && isAtRootLevel(cat))
        .map((cat) => cat.id || cat.Id),
    );

    const allDescendants = new Set(inactivRootIds);
    let changed = true;
    while (changed) {
      changed = false;
      for (const cat of categoriesState) {
        const catId = cat.id || cat.Id;
        const catParentId = cat.parentId || cat.FatherId;
        if (allDescendants.has(catParentId) && !allDescendants.has(catId)) {
          allDescendants.add(catId);
          changed = true;
        }
      }
    }
    return allDescendants;
  }, [categoriesState]);

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      ReactTooltip.rebuild();
    });
    return () => cancelAnimationFrame(frameId);
  }, [categoriesState, openSections]);

  return (
    <div className="category-edit admin-page admin-page--fill">
      <header className="admin-page__header">
        <div className="admin-page__header-start">
          <h1 className="title-role">ניהול מחלקות</h1>
        </div>
        <div className="admin-page__header-end">
          <div className="category-edit-search search admin-search-affix">
            <input
              className="admin-input"
              onChange={searchFun}
              value={search}
              type="text"
              placeholder="חיפוש מחלקה, תת-מחלקה או מוצר..."
            />
            {search ? (
              <button
                type="button"
                className="admin-search-affix__clear"
                onClick={clearSearch}
                aria-label="נקה חיפוש"
                title="נקה חיפוש"
              >
                <X aria-hidden size={18} strokeWidth={2} />
              </button>
            ) : (
              <span className="admin-search-affix__icon" aria-hidden>
                <SearchIcon size={18} strokeWidth={2} />
              </span>
            )}
          </div>
        </div>
      </header>
      <div className="admin-page__body category-edit-body">
        {isSearchActive && filteredCategories.length === 0 && (
          <p className="category-search-empty">
            לא נמצאו מחלקות התואמות לחיפוש
          </p>
        )}
        <ShowCategory
          categoriesState={filteredCategories}
          searchVisibleIds={searchVisibleIds}
        />
        <div>
          <button
            type="button"
            className="category-section-toggle"
            onClick={() => toggleSection("inactive")}
          >
            קטגוריות / מקבצים לא פעילים{openSections.inactive ? " ▲" : " ▼"}
          </button>
          {openSections.inactive && (
            <ShowCategory
              categoriesState={categoriesState?.filter((cat) => {
                const catId = cat.id || cat.Id;
                return allInactiveAndDescendants.has(catId);
              })}
              parentId="ROOT"
              level={0}
              isMainSection={false}
            />
          )}
        </div>
        <div>
          <button
            type="button"
            className="category-section-toggle"
            onClick={() => toggleSection("orphans")}
          >
            קטגוריות / מקבצים יתומים{openSections.orphans ? " ▲" : " ▼"}
          </button>
          {openSections.orphans && (
            <ShowCategory
              categoriesState={categoriesState?.filter(isOrphanCategory)}
              isNested={true}
              parentId={null}
              level={0}
              isMainSection={false}
            />
          )}
        </div>
      </div>
      <ReactTooltip id="addSubCategoryTip" place="top" effect="solid">
        הוספת תת קטגוריה
      </ReactTooltip>
      <ReactTooltip id="movingCategory" place="top" effect="solid">
        העברה לקטגורית אב אחרת
      </ReactTooltip>
      <ReactTooltip id="showHeaderTip" place="top" effect="solid">
        הצגת שם קטגוריה
      </ReactTooltip>
      <ReactTooltip id="viewCategoryProducts" place="top" effect="solid">
        הצגת כל המוצרים בקטגוריה
      </ReactTooltip>
    </div>
  );
};

export default CategoriesEdit;
